import { useState, useMemo } from 'react';
import { orders as allOrders } from '@/mocks/orders';
import type { Order } from '@/mocks/orders';
import OrderFilters from './components/OrderFilters';
import OrderTable from './components/OrderTable';
import OrderCard from './components/OrderCard';
import OrderDetailPanel from './components/OrderDetailPanel';
import NewOrderForm from './components/NewOrderForm';

export default function OrdersPage() {
  const [filteredOrders, setFilteredOrders] = useState<Order[]>(allOrders);
  const [viewMode, setViewMode] = useState<'table' | 'card'>('table');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showNewOrder, setShowNewOrder] = useState(false);

  const counts = useMemo(() => ({
    total: allOrders.length,
    filtered: filteredOrders.length,
  }), [filteredOrders]);

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-stone-900">Siparişler</h2>
          <p className="text-sm text-stone-500 mt-0.5">
            {counts.filtered === counts.total
              ? `Toplam ${counts.total} sipariş`
              : `${counts.filtered} / ${counts.total} sipariş listeleniyor`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex bg-stone-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all cursor-pointer ${
                viewMode === 'table' ? 'bg-white text-stone-800 shadow-sm' : 'text-stone-500 hover:text-stone-700'
              }`}
            >
              <i className="ri-table-line mr-1" />Tablo
            </button>
            <button
              onClick={() => setViewMode('card')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all cursor-pointer ${
                viewMode === 'card' ? 'bg-white text-stone-800 shadow-sm' : 'text-stone-500 hover:text-stone-700'
              }`}
            >
              <i className="ri-layout-grid-line mr-1" />Kart
            </button>
          </div>
          <button
            onClick={() => setShowNewOrder(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gold-500 text-white text-sm font-medium rounded-lg hover:bg-gold-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line" />
            Yeni Sipariş
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-5">
        <OrderFilters onFilter={setFilteredOrders} />
      </div>

      {/* Orders List */}
      {viewMode === 'table' ? (
        <OrderTable orders={filteredOrders} onSelect={setSelectedOrder} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filteredOrders.length === 0 ? (
            <div className="col-span-full py-12 text-center text-sm text-stone-400">
              <i className="ri-inbox-line text-2xl block mb-2" />
              Filtrelere uygun sipariş bulunamadı.
            </div>
          ) : (
            filteredOrders.map((order) => (
              <OrderCard key={order.id} order={order} onSelect={setSelectedOrder} />
            ))
          )}
        </div>
      )}

      {/* Detail Panel */}
      {selectedOrder && (
        <OrderDetailPanel order={selectedOrder} onClose={() => setSelectedOrder(null)} />
      )}

      {/* New Order Form */}
      {showNewOrder && (
        <NewOrderForm onClose={() => setShowNewOrder(false)} />
      )}
    </div>
  );
}